import type {
  CalendarClient,
  CalendarEvent,
  Calendar as CalendarModel,
} from '@wtfalch/calendar/calendar';
import {
  Button,
  Empty,
  Icon,
  MEDIA_QUERIES,
  Panes,
  Pill,
  placeOverlapping,
  useMediaQuery,
} from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';
import { ImportIcs } from './ImportIcs.tsx';
import {
  dayIndex,
  durationMinutes,
  eventBox,
  eventTier,
  HOUR_PX,
  weekHours,
  weekRangeLabel,
} from './layout.ts';
import type { Identity } from './participants.ts';
import { isUnanswered } from './participants.ts';
import { ShareCalendar } from './ShareCalendar.tsx';

/** The default hours; `weekHours` widens them to the week's own events. */
const HOURS = { start: 8, end: 19 };
const WEEKDAY_SHORT = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });
const HOUR_MIN = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** The hour (decimal) of the earliest timed event on `day`, or `null`. */
export function firstEventHour(events: readonly CalendarEvent[], day: Date): number | null {
  const hours = events
    .filter((e) => !e.isAllDay)
    .map((e) => new Date(e.start))
    .filter((d) => isSameDay(d, day))
    .map((d) => d.getHours() + d.getMinutes() / 60);
  return hours.length > 0 ? Math.min(...hours) : null;
}

export function Week({
  client,
  days,
  events,
  calendars,
  hidden,
  onToggleCalendar,
  onPrevWeek,
  onNextWeek,
  onToday,
  onNewEvent,
  onOpenEvent,
  identity,
  error,
  loading = false,
}: {
  client: CalendarClient;
  days: Date[];
  events: readonly CalendarEvent[];
  calendars: readonly CalendarModel[];
  hidden: ReadonlySet<string>;
  onToggleCalendar: (calendarId: string, visible: boolean) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onNewEvent: () => void;
  onOpenEvent: (eventId: string) => void;
  identity: Identity | null;
  error: string | null;
  /** The week's events are still on their way. */
  loading?: boolean;
}) {
  const wide = useMediaQuery(MEDIA_QUERIES.md);
  const now = new Date();
  const today = days.find((d) => isSameDay(d, now)) ?? null;
  // The narrow layout shows one day: today when it is in the week, else the
  // first; the person steps from there (see `AgendaDay`).
  const [pickedDay, setPickedDay] = useState<number | null>(null);
  const todayIndex = days.findIndex((d) => isSameDay(d, now));
  const dayIdx = pickedDay ?? Math.max(todayIndex, 0);
  // The grid opens at today's first event (the current time when today has
  // none, 08:00 on another week), not at its own first row -- once per week,
  // after the events have said how early the grid starts.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<number | null>(null);
  const weekKey = days[0]?.getTime() ?? 0;
  const gridStart = weekHours(events, HOURS).start;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading || scrolledFor.current === weekKey) return;
    scrolledFor.current = weekKey;
    const target = today
      ? (firstEventHour(events, now) ?? now.getHours() + now.getMinutes() / 60)
      : 8;
    el.scrollTop = Math.max(0, (target - gridStart) * HOUR_PX);
  });
  const shownDay = days[dayIdx] ?? days[0] ?? now;

  return (
    <Panes.Column flex className="cal-pane">
      <div className="flex flex-1 min-h-0 cal-body">
        {wide && (
          <aside className="cal-side">
            {calendars.length > 0 && (
              <div className="flex flex-col gap-2 cal-side-group">
                <span className="up">Calendars</span>
                {calendars.map((cal) => {
                  const checked = !hidden.has(cal.id);
                  return (
                    // The Share trigger sits *outside* `<label>`, as a
                    // sibling -- see `ShareCalendar`'s own doc comment for
                    // why a `<button>` before the checkbox, still inside
                    // the label, hijacked the label's implicit target
                    // (agora#158 review finding).
                    <div key={cal.id} className="flex items-center gap-2 h-7 cal-list-row">
                      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer cal-list-toggle">
                        <span
                          className="swatch"
                          style={{ background: cal.color || 'var(--border-strong)' }}
                        />
                        <span
                          className="flex-1 min-w-0 truncate text-sm cal-list-name"
                          title={cal.name}
                        >
                          {cal.name}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={(e) => onToggleCalendar(cal.id, e.target.checked)}
                        />
                        <span className="cal-cb" aria-hidden="true">
                          {checked && <Icon name="check" size={12} />}
                        </span>
                      </label>
                      <ShareCalendar client={client} calendar={cal} />
                    </div>
                  );
                })}
              </div>
            )}
          </aside>
        )}
        <div className="flex flex-1 flex-col min-w-0 min-h-0 cal-main">
          <div className="cal-head">
            {/* On a phone the day list below has its own arrows, by day; a second
                set here, by week, would stack on top of them. */}
            {wide && (
              <>
                <div className="flex gap-1 cal-nav">
                  <button
                    type="button"
                    className="cal-arrow"
                    aria-label="The week before"
                    onClick={onPrevWeek}
                  >
                    <Icon name="chev-left" size={16} />
                  </button>
                  <button
                    type="button"
                    className="cal-arrow"
                    aria-label="The week after"
                    onClick={onNextWeek}
                  >
                    <Icon name="chev-right" size={16} />
                  </button>
                </div>
                <h3 className="m-0 cal-title">{weekRangeLabel(days)}</h3>
              </>
            )}
            <Button
              kind="default"
              size="sm"
              onPress={() => {
                setPickedDay(null);
                onToday();
              }}
            >
              Today
            </Button>
            <div className="flex-1 cal-grow" />
            <ImportIcs client={client} calendars={calendars} />
            <Button kind="primary" size="sm" onPress={onNewEvent}>
              <Icon name="plus" size={16} />
              New event
            </Button>
          </div>
          {error && (
            <p className="cal-error text-bad" role="alert">
              {error}
            </p>
          )}
          {loading && !error && (
            <p className="cal-status text-xs text-muted" role="status">
              Loading the week…
            </p>
          )}
          {wide ? (
            <div ref={scrollRef} className="flex-1 min-w-0 overflow-auto cal-scroll">
              <CalGrid
                days={days}
                events={events}
                calendars={calendars}
                today={today}
                identity={identity}
                onOpenEvent={onOpenEvent}
              />
            </div>
          ) : (
            <div className="flex-1 min-w-0 overflow-auto py-4 px-5 cal-agenda">
              <AgendaDay
                day={shownDay}
                events={events}
                onPrev={() => {
                  if (dayIdx > 0) setPickedDay(dayIdx - 1);
                  else {
                    setPickedDay(6);
                    onPrevWeek();
                  }
                }}
                onNext={() => {
                  if (dayIdx < 6) setPickedDay(dayIdx + 1);
                  else {
                    setPickedDay(0);
                    onNextWeek();
                  }
                }}
                onOpenEvent={onOpenEvent}
              />
            </div>
          )}
        </div>
      </div>
    </Panes.Column>
  );
}

type Positioned = { event: CalendarEvent; start: Date; end: Date };

/** The week's own grid, matching the board's `.cal`/`.hours`/`.daycol`
 *  markup directly (`pages_agora.py`'s `week()`) -- `EventBlock` has no
 *  hook for a calendar's own colour, and that colour is the fill and the
 *  left edge the board draws, so this section owns its event blocks
 *  rather than reusing the design package's. */
function CalGrid({
  days,
  events,
  calendars,
  today,
  identity,
  onOpenEvent,
}: {
  days: Date[];
  events: readonly CalendarEvent[];
  calendars: readonly CalendarModel[];
  today: Date | null;
  identity: Identity | null;
  onOpenEvent: (eventId: string) => void;
}) {
  // Redrawn each minute, so the now-line and the "now" event follow the clock.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const now = new Date();
  const calendarById = new Map(calendars.map((c) => [c.id, c]));
  const hours = weekHours(events, HOURS);
  const hourRows = Array.from({ length: hours.end - hours.start }, (_, i) => hours.start + i);
  const allDay = events.filter((e) => e.isAllDay);

  return (
    // biome-ignore lint/a11y/useSemanticElements: a week is a grid of columns, not a form's fieldset -- role="group" with a name is the faithful one (the same call WeekGrid itself makes, @wtfalch/design's WeekGrid.js).
    <div
      className="flex flex-1 min-w-0 cal"
      role="group"
      aria-label={`Week of ${weekRangeLabel(days)}`}
    >
      <div className="hours">
        <div className="dayhead" aria-hidden="true" />
        {allDay.length > 0 && <div className="allrow" aria-hidden="true" />}
        {hourRows.map((h) => (
          <div className="relative h-14 hour" key={h} aria-hidden="true">
            <span>{`${String(h).padStart(2, '0')}:00`}</span>
          </div>
        ))}
      </div>
      {days.map((day, di) => {
        const dayEvents: Positioned[] = events
          .filter((e) => !e.isAllDay && dayIndex(e, days) === di)
          .map((e) => {
            const start = new Date(e.start);
            return {
              event: e,
              start,
              end: new Date(start.getTime() + durationMinutes(e.duration) * 60_000),
            };
          });
        const placed = placeOverlapping(dayEvents);
        const isToday = today !== null && isSameDay(day, today);

        return (
          <div className={`daycol${di > 4 ? ' weekend' : ''}`} key={day.toISOString()}>
            <div className={`dayhead${isToday ? ' today' : ''}`}>
              <span className="text-xs text-muted">{WEEKDAY_SHORT.format(day)}</span>
              <span className="dnum">{day.getDate()}</span>
            </div>
            {allDay.length > 0 && (
              <div className="allrow">
                {allDay
                  .filter((e) => dayIndex(e, days) === di)
                  .map((e) => (
                    <button
                      type="button"
                      key={e.id}
                      className="ev-all"
                      style={
                        {
                          '--ev-color':
                            calendarById.get(e.calendarId)?.color || 'var(--border-strong)',
                        } as React.CSSProperties
                      }
                      onClick={() => onOpenEvent(e.id)}
                    >
                      {e.title}
                    </button>
                  ))}
              </div>
            )}
            <div className="relative slots" style={{ height: hourRows.length * HOUR_PX }}>
              {hourRows.map((h) => (
                <div className="slot" key={h} />
              ))}
              {isToday && now.getHours() >= hours.start && now.getHours() < hours.end && (
                <div
                  className="nowline"
                  aria-hidden="true"
                  style={{
                    top: (now.getHours() + now.getMinutes() / 60 - hours.start) * HOUR_PX,
                  }}
                />
              )}
              {placed.map(({ event: positioned, column, columns }) => {
                const { event, start, end } = positioned;
                const minutes = durationMinutes(event.duration);
                const tier = eventTier(minutes);
                const box = eventBox(start, end, hours.start);
                const calendar = calendarById.get(event.calendarId);
                const unanswered = isUnanswered(event, identity);
                const isNow = start <= now && now < end;
                const time = HOUR_MIN.format(start);
                const timeRange = `${time}–${HOUR_MIN.format(end)}`;
                const subtitle = event.location || calendar?.name;

                return (
                  <button
                    type="button"
                    key={event.id}
                    className={['ev', tier, unanswered ? 'unanswered' : '', isNow ? 'now' : '']
                      .filter(Boolean)
                      .join(' ')}
                    style={
                      {
                        top: box.top,
                        height: box.height,
                        left: `calc(${(column / columns) * 100}% + 4px)`,
                        width: `calc(${100 / columns}% - 8px)`,
                        '--ev-color': calendar?.color || 'var(--border-strong)',
                      } as React.CSSProperties
                    }
                    onClick={() => onOpenEvent(event.id)}
                    title={`${timeRange} ${event.title}`}
                  >
                    {tier === 'tiny' ? (
                      <span className="flex min-w-0 items-baseline gap-2 evline">
                        <span className="evat">{time}</span>
                        <span className="evt">{event.title}</span>
                      </span>
                    ) : (
                      <>
                        <span className="evat">
                          {time}
                          <span className="evat-end">–{HOUR_MIN.format(end)}</span>
                        </span>
                        <span className="evt">{event.title}</span>
                        {tier === 'tall' && subtitle && (
                          <span className="truncate text-muted text-2xs evw">{subtitle}</span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dayLabel(day: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(day);
}

/** One day of the week as a list, for a window too narrow for seven
 *  columns. Each row opens its event, and the person steps a day at a time
 *  (past the ends, into the next or the previous week). */
function AgendaDay({
  day,
  events,
  onPrev,
  onNext,
  onOpenEvent,
}: {
  day: Date;
  events: readonly CalendarEvent[];
  onPrev: () => void;
  onNext: () => void;
  onOpenEvent: (eventId: string) => void;
}) {
  const now = new Date();
  const rows = events
    .filter((e) => isSameDay(new Date(e.start), day))
    .map((e) => {
      const at = new Date(e.start);
      return { event: e, at, end: at.getTime() + durationMinutes(e.duration) * 60_000 };
    })
    .sort((a, b) => a.at.getTime() - b.at.getTime());
  const next = rows.find((r) => r.end > now.getTime())?.event.id;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <button type="button" className="cal-arrow" aria-label="The day before" onClick={onPrev}>
          <Icon name="chev-left" size={16} />
        </button>
        <h3 className="m-0 flex-1 cal-title">{dayLabel(day)}</h3>
        <button type="button" className="cal-arrow" aria-label="The day after" onClick={onNext}>
          <Icon name="chev-right" size={16} />
        </button>
      </div>
      {rows.length === 0 ? (
        <Empty icon="calendar">Nothing this day.</Empty>
      ) : (
        <ul aria-label={dayLabel(day)} className="flex flex-col list-none m-0 p-0">
          {rows.map(({ event, at }) => (
            <li key={event.id}>
              <button
                type="button"
                className="flex gap-3 items-start py-3 w-full cal-agenda-row"
                onClick={() => onOpenEvent(event.id)}
              >
                <time
                  dateTime={at.toISOString()}
                  className="text-xs text-muted w-10 flex-none pt-1"
                >
                  {event.isAllDay ? 'All day' : HOUR_MIN.format(at)}
                </time>
                <span className="flex-1 min-w-0 flex flex-col gap-1 cal-agenda-body">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-strong truncate">{event.title}</span>
                    {event.id === next && <Pill tone="good">Next</Pill>}
                  </span>
                  {event.location && <span className="text-xs text-muted">{event.location}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
